import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type {
  MetricCol,
  MetricKey,
  SourceType,
  SummaryCardTranslationKey,
  ViewTab,
} from "../types";

import { PERFORMANCE_REPORT_MIN_DATE } from "../urlParams/schema";

export const usePerformanceReportTranslations = () => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  return useMemo(() => {
    const tabs: { label: string; value: ViewTab }[] = [
      {
        label: t("performanceReport_tab_source", "Source"),
        value: "source",
      },
      {
        label: t("performanceReport_tab_country", "Country"),
        value: "country",
      },
      {
        label: t("performanceReport_tab_rebate", "Rebate program"),
        value: "rebate",
      },
      {
        label: t("performanceReport_tab_campaign", "Campaign"),
        value: "campaign",
      },
    ];

    const sourceColumnLabels: Record<ViewTab, string> = {
      source: t("performanceReport_column_source", "Source"),
      country: t("performanceReport_column_country", "Country"),
      rebate: t("performanceReport_column_rebate", "Rebate program"),
      campaign: t("performanceReport_column_campaign", "Campaign"),
    };

    const filterSourceLabels: Record<SourceType, string> = {
      Links: t("performanceReport_filterSource_links", "Links"),
      Banners: t("performanceReport_filterSource_banners", "Banners"),
    };

    const summaryCardLabels: Record<SummaryCardTranslationKey, string> = {
      performanceReport_column_clicks: t(
        "performanceReport_column_clicks",
        "Clicks",
      ),
      performanceReport_column_registration: t(
        "performanceReport_column_registration",
        "Registered",
      ),
      performanceReport_column_activeClients: t(
        "performanceReport_column_activeClients",
        "Active clients",
      ),
      performanceReport_column_tradingVolume: t(
        "performanceReport_column_tradingVolume",
        "Trading volume",
      ),
      performanceReport_column_earnings: t(
        "performanceReport_column_earnings",
        "Total earnings",
      ),
    };

    const metricColumns: MetricCol[] = [
      {
        key: "impressions",
        label: t("performanceReport_column_impressions", "Impressions"),
        type: "int",
      },
      {
        key: "clicks",
        label: t("performanceReport_column_clicks", "Clicks"),
        type: "int",
      },
      {
        key: "registration",
        label: t("performanceReport_column_registration", "Registered"),
        type: "int",
      },
      {
        key: "approved",
        label: t("performanceReport_column_approved", "Approved"),
        type: "int",
      },
      {
        key: "funded",
        label: t("performanceReport_column_funded", "Funded"),
        type: "int",
      },
      {
        key: "traded",
        label: t("performanceReport_column_traded", "Traded"),
        type: "int",
      },
      {
        key: "cr_reg",
        label: t("performanceReport_column_regRate", "Reg %"),
        menuLabel: t(
          "performanceReport_column_clickToRegistrationRate",
          "Click to registration rate",
        ),
        type: "pct",
        num: "registration",
      },
      {
        key: "cr_app",
        label: t("performanceReport_column_appRate", "App %"),
        menuLabel: t(
          "performanceReport_column_clickToApprovedRate",
          "Click to approved rate",
        ),
        type: "pct",
        num: "approved",
      },
      {
        key: "cr_funded",
        label: t("performanceReport_column_fundedRate", "Fnd %"),
        menuLabel: t(
          "performanceReport_column_clickToFundedRate",
          "Click to funded rate",
        ),
        type: "pct",
        num: "funded",
      },
      {
        key: "cr_traded",
        label: t("performanceReport_column_tradedRate", "Trd %"),
        menuLabel: t(
          "performanceReport_column_clickToTradedRate",
          "Click to traded rate",
        ),
        type: "pct",
        num: "traded",
      },
      {
        key: "volume",
        label: t("performanceReport_column_tradingVolume", "Trading volume"),
        type: "usd",
      },
      {
        key: "earnings",
        label: t("performanceReport_column_earnings", "Total earnings"),
        type: "usd",
      },
    ];

    const conversionTips: Partial<Record<MetricKey, string>> = {
      cr_reg: t(
        "performanceReport_conversionTip_registration",
        "The conversion rate percentage of clients from click to registration",
      ),
      cr_app: t(
        "performanceReport_conversionTip_approved",
        "The conversion rate percentage of clients from click to approved",
      ),
      cr_funded: t(
        "performanceReport_conversionTip_funded",
        "The conversion rate percentage of clients from click to funded",
      ),
      cr_traded: t(
        "performanceReport_conversionTip_traded",
        "The conversion rate percentage of clients from click to traded",
      ),
    };

    return {
      headerTitle: t("performanceReport_header_title", "Your performance"),
      headerLastUpdated: (date: string) =>
        t(
          "performanceReport_header_lastUpdated",
          "Performance statistics are updated every hour. Last updated: {{date}}",
          { date },
        ),
      datepickerToday: t("performanceReport_datepicker_today", "Today"),
      datepickerYesterday: t(
        "performanceReport_datepicker_yesterday",
        "Yesterday",
      ),
      datepickerPast: (period: number) =>
        t("performanceReport_datepicker_past", "Last {{period}} days", {
          period,
        }),
      datepickerCurrentMonth: t(
        "performanceReport_datepicker_currentMonth",
        "This month",
      ),
      datepickerPastMonth: t(
        "performanceReport_datepicker_pastMonth",
        "Last month",
      ),
      dateRangeLimitation: t(
        "performanceReport_datepicker_limitation",
        "You can only select data up to one year at a time. The latest data available for this period is from {{date}}.",
        {
          date: PERFORMANCE_REPORT_MIN_DATE.toLocaleDateString(locale, {
            day: "numeric",
            month: "short",
            year: "numeric",
          }),
        },
      ),
      datepickerCancel: t("performanceReport_datepicker_cancel", "Cancel"),
      datepickerApply: t("performanceReport_datepicker_apply", "Apply"),
      filterButton: t("performanceReport_filterButton", "Filter"),
      viewBy: t("performanceReport_viewBy", "View by"),
      exportButton: t("performanceReport_exportButton", "Export CSV"),
      tabs,
      sourceColumnLabels,
      summaryCardLabels,
      metricColumns,
      conversionTips,
      unitUsd: t("performanceReport_unit_usd", "USD"),
      columnsButton: t("performanceReport_columnsButton", "Columns"),
      showColumns: t("performanceReport_showColumns", "Show columns"),
      conversionRate: t("performanceReport_conversionRate", "Conversion rate"),
      mobileMetricsTriggerLabel: t(
        "performanceReport_mobile_metricsTriggerLabel",
        "Metrics",
      ),
      mobileViewMetricsTitle: t(
        "performanceReport_mobile_viewMetricsTitle",
        "View metrics",
      ),
      filterTitle: t("performanceReport_filterTitle", "Filter"),
      filterClear: t("performanceReport_filterClear", "Clear"),
      filterApply: t("performanceReport_filterApply", "Apply"),
      filterSource: t("performanceReport_filterSource", "Source"),
      filterSourceLabels,
      filterLandingPageLabel: t(
        "performanceReport_filterLandingPage",
        "Landing page",
      ),
      filterLandingPagePlaceholder: t(
        "performanceReport_filterLandingPagePlaceholder",
        "Select landing page",
      ),
      filterBannerGroupLabel: t(
        "performanceReport_filterBannerGroup",
        "Banner group",
      ),
      filterBannerGroupPlaceholder: t(
        "performanceReport_filterBannerGroupPlaceholder",
        "Select banner group",
      ),
      filterRebateProgramLabel: t(
        "performanceReport_filterRebateProgram",
        "Rebate program",
      ),
      filterRebateProgramPlaceholder: t(
        "performanceReport_filterRebateProgramPlaceholder",
        "Select rebate program",
      ),
      countryUnknown: t("performanceReport_country_unknown", "Unknown"),
      noCampaign: t("performanceReport_noCampaign", "No campaign"),
      filterCountryLabel: t("performanceReport_filterCountry", "Country"),
      filterCountryPlaceholder: t(
        "performanceReport_filterCountryPlaceholder",
        "Select country",
      ),
      filterCampaignLabel: t("performanceReport_filterCampaign", "Campaign"),
      filterCampaignPlaceholder: t(
        "performanceReport_filterCampaignPlaceholder",
        "Select campaign",
      ),
      // Copy is table-scoped, not page-scoped: the error no longer replaces the
      // whole page, it replaces the table body while the header and controls
      // stay live. Wording is taken from the design project's copy registry
      // (`js/copy-registry.js` → error.title / error.desc / error.retry).
      errorTitle: t("performanceReport_error_title", "Unable to load data"),
      errorText: t(
        "performanceReport_error_text",
        "Refresh the table to try again",
      ),
      errorActionText: t("performanceReport_error_action", "Refresh"),
      emptyTitle: t("performanceReport_empty_title", "No data available"),
      emptyText: t(
        "performanceReport_empty_text",
        "Try changing your filters or adding new clients",
      ),
      paginationLabelForShowing: t(
        "common_pagination_label_showing",
        "Showing",
      ),
      paginationLabelForOf: t("common_pagination_label_of", "of"),
      mobileFilterAriaLabel: t(
        "performanceReport_mobile_filterAriaLabel",
        "Open filters",
      ),
      mobileDateAriaLabel: t(
        "performanceReport_mobile_dateAriaLabel",
        "Open date range",
      ),
      mobileReset: t("performanceReport_mobile_reset", "Reset"),
      mobileDateRangeTitle: t(
        "performanceReport_mobile_dateRangeTitle",
        "Date range",
      ),
      mobileCustomDateRange: t(
        "performanceReport_mobile_customDateRange",
        "Custom date range",
      ),
      mobileDetailTitle: t(
        "performanceReport_mobile_detailTitle",
        "Performance detail",
      ),
      mobileDetailTraffic: t(
        "performanceReport_mobile_detailTraffic",
        "Traffic statistics",
      ),
      mobileDetailTradingSummary: t(
        "performanceReport_mobile_detailTradingSummary",
        "Trading summary",
      ),
      mobileDetailRebateAccount: t(
        "performanceReport_mobile_detailRebateAccount",
        "Rebate account",
      ),
      formatDateRange: (start: Date | null, end: Date | null): string => {
        if (!start || !end) return "";
        // Design calls for "01 Jul 2026", but `toLocaleDateString` returns the
        // locale's own order — "Jul 01, 2026" under en-US. Reassembling from
        // `formatToParts` pins day-month-year while keeping the month name
        // localized, rather than hardcoding an en-GB locale.
        const fmt = (d: Date) => {
          const parts = new Intl.DateTimeFormat(locale, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          }).formatToParts(d);
          const part = (type: Intl.DateTimeFormatPartTypes) =>
            parts.find((p) => p.type === type)?.value ?? "";
          return `${part("day")} ${part("month")} ${part("year")}`;
        };
        return `${fmt(start)} – ${fmt(end)}`;
      },
    };
  }, [t, locale]);
};

export type PerformanceReportTranslations = ReturnType<
  typeof usePerformanceReportTranslations
>;
